import React, { useCallback, useRef, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import axios from 'axios';
import config from '../config';

// A prediction from GET /api/accounting/address-lookup (Google Places Autocomplete) — no
// coordinates yet, since Autocomplete predictions never carry them. Resolved to a real
// AddressMatch (below) via GET /api/accounting/address-details once the user picks one.
interface AddressPrediction {
  place_id: string;
  label: string;
  short_label: string;
}

// The fully-resolved shape passed to onSelect, mirroring backend/google_places_service.py's
// normalized result exactly.
export interface AddressMatch {
  label: string;
  short_label: string;
  latitude: number;
  longitude: number;
  place_id: string | null;
  postcode: string | null;
  country: string | null;
}

interface AddressFinderProps {
  label: string;
  placeholder?: string;
  value: string;
  onInputChange: (value: string) => void;
  // Called with a real match right after the user picks a suggestion, or null the moment they
  // start typing again / clear the field — "verified" only ever means "just picked from the
  // list", never a guess about free-typed text.
  onSelect: (match: AddressMatch | null) => void;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

// freeSolo MUI Autocomplete backed by GET /api/accounting/address-lookup (Google Places) — type
// a postcode or address, pick a real suggestion (resolves to lat/lng via a follow-up
// /address-details call, then fires onSelect), or keep typing your own (stays unverified, no
// coordinates). Used both for a fresh building's address (Add flow) and the Edit dialog's
// standalone "Set location" action.
const AddressFinder: React.FC<AddressFinderProps> = ({
  label, placeholder, value, onInputChange, onSelect, helperText, disabled, size = 'small',
}) => {
  const [options, setOptions] = useState<AddressPrediction[]>([]);
  const [loading, setLoading] = useState(false);
  // Resolving the picked prediction's real coordinates (the /address-details round trip) —
  // separate from `loading` (which is the autocomplete-as-you-type spinner) since this fires
  // once, right after a selection, not on every keystroke.
  const [resolving, setResolving] = useState(false);
  // 503 from either proxy means GOOGLE_MAPS_API_KEY isn't configured yet — free typing must
  // still work, so this is a quiet helper-text note, not an error state.
  const [unavailable, setUnavailable] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a slow earlier request overwriting the results of a newer keystroke's
  // request that resolved first.
  const requestIdRef = useRef(0);

  const search = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const res = await axios.get(`${config.apiUrl}/api/accounting/address-lookup`, { params: { query } });
        if (requestId !== requestIdRef.current) return;
        setOptions(res.data?.results || []);
        setUnavailable(false);
      } catch (err: any) {
        if (requestId !== requestIdRef.current) return;
        setOptions([]);
        setUnavailable(err?.response?.status === 503);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
  }, []);

  const resolveAndSelect = async (prediction: AddressPrediction) => {
    setResolving(true);
    try {
      const res = await axios.get(`${config.apiUrl}/api/accounting/address-details`, {
        params: { place_id: prediction.place_id },
      });
      const result = res.data?.result;
      if (!result) { onSelect(null); return; }
      onSelect({
        label: prediction.label,
        short_label: prediction.short_label,
        latitude: result.latitude,
        longitude: result.longitude,
        place_id: result.place_id,
        postcode: result.postcode,
        country: result.country,
      });
    } catch {
      onSelect(null);
    } finally {
      setResolving(false);
    }
  };

  return (
    <Autocomplete
      freeSolo
      size={size}
      disabled={disabled}
      options={options}
      loading={loading || resolving}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === 'string' ? option : (option.short_label || option.label))}
      inputValue={value}
      onInputChange={(_, newValue, reason) => {
        onInputChange(newValue);
        if (reason === 'input') {
          // Editing away from a previously-picked match invalidates it immediately — verified
          // only ever means "the currently shown text is exactly what was just picked".
          onSelect(null);
          search(newValue);
        } else if (reason === 'clear') {
          onSelect(null);
          setOptions([]);
        }
      }}
      onChange={(_, newValue) => {
        if (newValue && typeof newValue !== 'string') {
          resolveAndSelect(newValue);
        } else {
          onSelect(null);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={unavailable
            ? 'Address lookup unavailable right now — you can still type your own.'
            : helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || resolving) ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AddressFinder;
